import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import QuestPanel, { type QuestState } from '../components/QuestPanel'

interface Overview {
  day: string
  quests: QuestState[]
  patrolAvailable: number
  artifacts: { owned: number; total: number }
}

interface Summary {
  game: {
    totalXp: number
    level: number
    xpIntoLevel: number
    xpForNextLevel: number
    progress: number
    streak: number
    longestStreak: number
  }
  counts: { lessons_done: number; lessons_total: number; quizzes_taken: number }
  recent: { kind: string; amount: number; at: number }[]
}

export default function Dashboard() {
  const { user } = useAuth()
  const [data, setData] = useState<Summary | null>(null)
  const [overview, setOverview] = useState<Overview | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    void api<Summary>('/progress/summary', { signal: controller.signal })
      .then(setData)
      .catch(() => {})
    void api<Overview>('/game/overview', { signal: controller.signal })
      .then(setOverview)
      .catch(() => {})
    return () => controller.abort()
  }, [])

  if (!data) return <div className="loading">Loading your climb…</div>

  return (
    <div className="dashboard">
      <h1>Welcome back, {user?.displayName ?? user?.email.split('@')[0]}</h1>

      {overview && (
        <QuestPanel quests={overview.quests} patrolAvailable={overview.patrolAvailable} />
      )}

      <div className="stat-grid">
        <Stat label="Level" value={data.game.level} sub={`${data.game.totalXp} XP total`} />
        <Stat label="Streak" value={`🔥 ${data.game.streak}`} sub={`best: ${data.game.longestStreak}`} />
        <Stat
          label="Lessons"
          value={`${data.counts.lessons_done} / ${data.counts.lessons_total}`}
          sub="completed"
        />
        <Stat label="Quizzes" value={data.counts.quizzes_taken} sub="attempts graded" />
        {overview && (
          <Link to="/collection" className="stat stat-link">
            <span className="stat-label">Artifacts</span>
            <span className="stat-value">
              {overview.artifacts.owned} / {overview.artifacts.total}
            </span>
            <span className="dim">collection →</span>
          </Link>
        )}
      </div>

      <section className="level-progress">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${Math.round(data.game.progress * 100)}%` }} />
        </div>
        <p className="dim">
          {data.game.xpIntoLevel} / {data.game.xpForNextLevel} XP to level {data.game.level + 1}
        </p>
      </section>

      <section>
        <h2>Recent XP</h2>
        {data.recent.length === 0 ? (
          <p className="dim">Nothing yet. Open a lesson and finish it.</p>
        ) : (
          <ul className="ledger">
            {data.recent.map((e, i) => (
              <li key={i}>
                <span className={`kind kind-${e.kind}`}>{e.kind}</span>
                <span>+{e.amount} XP</span>
                <span className="dim">{new Date(e.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="dim">
          This list is read straight from <code>xp_events</code>, the append-only ledger. Your
          level is computed from it, never stored as an editable number.
        </p>
      </section>
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {sub && <span className="dim">{sub}</span>}
    </div>
  )
}
